package io.github.jpuf.portfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.Tooltip
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.style.vars.color.ColorVar
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.shapes.Rect
import com.varabyte.kobweb.silk.theme.shapes.clip
import io.github.jpuf.portfolio.*
import io.github.jpuf.portfolio.components.layouts.PageLayout
import io.github.jpuf.portfolio.components.widgets.IconButton
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span


@Page
@Composable
fun HomePage() {
    PageLayout("Home") {
        Column(
            modifier = HeroContainerStyle.toModifier().gap(0.5.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Desktop layout: Row with image on left, details on right
            Row(
                modifier = Modifier.fillMaxWidth().displayIfAtLeast(Breakpoint.MD),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Image("/head.webp", height = 200, width = 200, modifier = Modifier.clip(Rect(5.px)))
                NameDetails()
            }

            // Mobile layout: Column with centered content and smaller image
            Column(
                modifier = Modifier.fillMaxWidth().displayUntil(Breakpoint.MD).gap(1.cssRem),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Image("/head.webp", height = 150, width = 150, modifier = Modifier.clip(Rect(5.px)))
                NameDetails(modifier = Modifier.fillMaxWidth())
            }

            Div(BodyTextStylePadded.toAttrs()) {
                Span {
                    SpanText("I'm a Mobile Engineer for Decathlon.\nI've been lucky enough to have lived and worked in Manchester, Amsterdam, and now Paris :-]\n\nTake a look at some of my ")
                    Link(
                        path = "/photography",
                        text = "photography",
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        modifier = Modifier.fontFamily("VG5000")
                            .setVariable(ColorVar, ColorMode.current.toSitePalette().brand.accent),
                        variant = UncoloredLinkVariant
                    )
                    SpanText("!")
                }
            }
        }
    }
}

@Composable
private fun NameDetails(modifier: Modifier = Modifier) {
    // Desktop version: Right-aligned
    Column(
        modifier = modifier.displayIfAtLeast(Breakpoint.MD),
        horizontalAlignment = Alignment.End,
        verticalArrangement = Arrangement.spacedBy(0.8.cssRem)
    ) {
        Div(HeadlineUnderlinedTextStyle.toAttrs()) {
            SpanText("James Bennett")
        }

        Div(SubheadlineTextStyle.toAttrs()) {
            SpanText("Android Engineer")
        }

        Div {
            Row(
                horizontalArrangement = Arrangement.spacedBy(2.cssRem),
            ) {
                val iconSize = IconSize.X2
                val color = ColorMode.current.toPalette().color.toRgb().copyf(alpha = 0.8f)
                SocialLinkButton("https://github.com/JPUF/", "/JPUF") {
                    FaGithub(size = iconSize, modifier = Modifier.color(color = color))
                }
                SocialLinkButton("https://www.linkedin.com/in/james-bennett-apps/", "/james-bennett-apps") {
                    FaLinkedin(size = iconSize, modifier = Modifier.color(color = color))
                }
                SocialLinkButton("https://www.inaturalist.org/people/1782634", "iNaturalist") {
                    FaLeaf(size = iconSize, modifier = Modifier.color(color = color))
                }
                EmailButton(iconSize = iconSize)
            }
        }
    }

    // Mobile version: Center-aligned
    Column(
        modifier = modifier.displayUntil(Breakpoint.MD),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(0.8.cssRem)
    ) {
        Div(HeadlineUnderlinedTextStyle.toAttrs()) {
            SpanText("James Bennett")
        }

        Div(SubheadlineTextStyle.toAttrs()) {
            SpanText("Android Engineer")
        }

        Div {
            Row(
                horizontalArrangement = Arrangement.spacedBy(1.0.cssRem),
            ) {
                val iconSize = IconSize.LG
                val color = ColorMode.current.toPalette().color.toRgb().copyf(alpha = 0.8f)
                SocialLinkButton("https://github.com/JPUF/", "/JPUF") {
                    FaGithub(size = iconSize, modifier = Modifier.color(color = color))
                }
                SocialLinkButton("https://www.linkedin.com/in/james-bennett-apps/", "/james-bennett-apps") {
                    FaLinkedin(size = iconSize, modifier = Modifier.color(color = color))
                }
                SocialLinkButton("https://www.inaturalist.org/people/1782634", "iNaturalist") {
                    FaLeaf(size = iconSize, modifier = Modifier.color(color = color))
                }
                EmailButton(iconSize = iconSize)
            }
        }
    }
}

@Composable
private fun SocialLinkButton(
    path: String,
    tooltipText: String,
    content: @Composable () -> Unit
) {
    val ctx = rememberPageContext()
    IconButton(
        modifier = Modifier.onMouseEnter {
        },
        onClick = {
            ctx.router.navigateTo(path)
        }
    ) {
        content()
    }

    Tooltip(
        target = ElementTarget.PreviousSibling,
        placement = PopupPlacement.Bottom,
        variant = TooltipCentreStyle,
        text = tooltipText,
    )
}

@Composable
private fun EmailButton(iconSize: IconSize = IconSize.X2) {
    val email = "2009bennettj@gmail.com"
    var copied by remember { mutableStateOf(false) }
    var isHovered by remember { mutableStateOf(false) }
    IconButton(
        modifier = Modifier.onMouseEnter {
            isHovered = true
        }.onMouseLeave {
            isHovered = false
            copied = false
        },
        onClick = {
            runCatching {
                js("navigator.clipboard.writeText")(email)
                copied = true
            }
        }
    ) {
        val color = ColorMode.current.toPalette().color.toRgb().copyf(alpha = 0.8f)
        when {
            isHovered && copied -> FaCheck(size = iconSize, modifier = Modifier.color(color = color))
            isHovered -> FaCopy(size = iconSize, modifier = Modifier.color(color = color))
            else -> FaEnvelope(size = iconSize, modifier = Modifier.color(color = color))
        }
    }

    if (copied) {
        LaunchedEffect(copied) {
            kotlinx.coroutines.delay(1500)
            copied = false
        }
    }

    Tooltip(
        target = ElementTarget.PreviousSibling,
        placement = PopupPlacement.BottomRight,
        variant = TooltipCentreStyle,
        text = email,
    )
}